import Button from 'core-system/Button/Button'
import PlatformSvg from 'core-system/Icons/PlatformSvg'
import { TableRow, TableRowCell } from 'core-system/Table'
import Table from 'core-system/Table/Table'
import Text from 'core-system/Text/Text'
import moment, { Moment } from 'moment'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import FormattingUtils from 'shared/FormattingUtils'
import styled from 'styled-components'
import AddOTRView from './AddOTRView'
import NoPaymentModal from 'shared/components/NoPaymentModal'

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 2rem;
`

const HeaderButtons = styled.div`
  display: flex;
  flex-direction: row;
`

const UserContainer = styled.div`
  display: flex;
`

const NameContainer = styled.div`
  margin-left: 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
`

interface formattedOTR {
  amount: number
  createdAt: Moment
  employeeCorpId: string
  employeeId: string
  employeeName: string
  id: string
  memo: string
}

const getTableRows = (formattedOTRData: formattedOTR[]) => {
  return [...formattedOTRData]
    .sort((a, b) => (b.createdAt.isBefore(a.createdAt) ? -1 : 1))
    .map((otr, idx) => {
      return (
        <TableRow key={idx}>
          <TableRowCell>{otr.createdAt.format('MMMM D, YYYY')}</TableRowCell>
          <TableRowCell>
            <UserContainer>
              <PlatformSvg
                folder='users'
                variant='userBasic'
                width={40}
                height={40}
              />
              <NameContainer>
                <Text variant='action4' hideOverflow>
                  {otr.employeeName}
                </Text>
                <Text variant='body2'>
                  {otr.employeeCorpId ? `#${otr.employeeCorpId}` : ''}
                </Text>
              </NameContainer>
            </UserContainer>
          </TableRowCell>
          <TableRowCell>
            {FormattingUtils.formatDollar(otr.amount)}
          </TableRowCell>
          <TableRowCell>{otr.memo}</TableRowCell>
        </TableRow>
      )
    })
}

const OTRHistoryView = React.memo(() => {
  const { profile, hasValidPaymentMethod } = useSelector(
    (state: AppState) => state.employer
  )

  const formattedSegmentOTRs = useSelector(
    (state: AppState) => state.incentives.allOneTimeRewards
  )?.map((otr) => {
    return {
      ...otr,
      createdAt: moment(otr.createdAt),
      employeeName: otr.employeeName,
      employeeCorpId: otr.employeeCorporateId,
    }
  })

  const [showAddOTRView, setShowAddOTRView] = useState<boolean>(false)
  const [showNoPaymentModal, setShowNoPaymentModal] = useState<boolean>(false)

  const handleAddNewOTR = () => {
    if (hasValidPaymentMethod || !profile?.openRegistration) {
      setShowAddOTRView(!showAddOTRView)
    } else {
      setShowNoPaymentModal(true)
    }
  }

  const downloadOtrCsv = () => {
    dispatch(dashboardActions.getDashboardCsvReport({ reportType: 'otr' }))
    SegmentService.track('otr-history-download')
  }

  const HeaderCell = (text: string) => (
    <TableRowCell>
      <Text fontWeight={600} variant={'action3'}>
        {text}
      </Text>
    </TableRowCell>
  )

  const headerCells = [
    HeaderCell('Award Date'),
    HeaderCell('Commuter'),
    HeaderCell('Amount'),
    HeaderCell('Description'),
  ]

  return (
    <>
      {showAddOTRView ? (
        <AddOTRView setShowAddOTRView={setShowAddOTRView} />
      ) : (
        <>
          <HeaderContainer>
            <Text variant='h2'>One Time Reward History</Text>
            <HeaderButtons>
              <Button onClick={() => handleAddNewOTR()}>
                Add One Time Rewards
              </Button>
              {/* <Button
                disabled={formattedSegmentOTRs.length === 0}
                onClick={() => downloadOtrCsv()}
                marginLeft='1rem'
              >
                CSV
              </Button> */}
            </HeaderButtons>
          </HeaderContainer>
          <Table
            headerCells={headerCells}
            maxRows={10}
            emptyMessage='No One Time Rewards Awarded'
            rows={getTableRows(formattedSegmentOTRs)}
          />
          <NoPaymentModal
            open={showNoPaymentModal}
            closeModal={() => setShowNoPaymentModal(false)}
            product='otr'
          />
        </>
      )}
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  OTRHistoryView.displayName = 'OTRHistoryView'
}

export default OTRHistoryView
